import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { RAID_METADATA_BY_SLUG } from '../../../../shared/enums';
import { RouteComponentProps } from '../../../../shared/types/common';
import { safeParseFloat, safeParseInt } from '../../../../../../shared/common/utils';
import { navigateTo } from '../../../../../../shared/frontend/libs/history';
import { apiServiceAsync } from '../../services/apiService';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { processXCCEarnings, XCC_ALL_BOSSES, XCC_CHECKPOINT_BY_BOSS_AND_RAID } from './xccEventData';
import Numeral from 'numeral';
import { ComplexTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { TEST_PAYLOAD } from '../../../fixtures/widgets/versus-timeline-widget-data';
import { useWidgetVersion } from '../hooks/useWidgetVersion';
import { Expansions } from '../../../../../../shared/common/enums';

/* tslint:disable:max-line-length */

type TStreamWidgetVersusProps = RouteComponentProps<any, any>;

type TSingleGuildProps = any & {
  side: 'left' | 'right' | 'center';
  guild: any;
  hideEarnings: boolean;
  className?: string;
};

type TTimelineWidgetRowProps = {
  guildLogo: string;
  guildName: string;
  guild: any;
  placementByBoss: any;
  lifelines: any[];
  hideEarnings: boolean;
  side: 'top' | 'bottom';
};

const TimelineWidgetRow: FC<TTimelineWidgetRowProps> = (props: any) => {
  return (
    <div className={cx(`versus-timeline-widget--side-${props.side}`, 'slds-grid slds-grid--vertical-align-center')}>
      <div className="versus-timeline-widget--guild-container slds-col slds-grow-none slds-text-align--center">
        <img src={props.guildLogo} className="versus-timeline-widget--guild-logo" />
        <div className="versus-timeline-widget--guild-name">{props.guildName}</div>
      </div>
      <div className="slds-col slds-p-horizontal--x-small">
        <SingleGuild
          lifelines={props.lifelines}
          placementByBoss={props.placementByBoss}
          side={props.side}
          hideEarnings={props.hideEarnings}
          {...props.guild}
        />
      </div>
    </div>
  );
};

function CheckpointDetails(props: any) {
  const cp = props.checkpoint;

  return (
    <>
      <div className="checkpoint-title">{cp.name}</div>
      {!props.hideEarnings && (
        <div className="checkpoint-earnings">${Numeral(cp.prizes[props.place - 1]).format('0,0')}</div>
      )}
    </>
  );
}

function SingleGuild(props: TSingleGuildProps) {
  const columns: any[] = [];

  for (const defeatedEncounter of props.encountersDefeated) {
    const cp = XCC_CHECKPOINT_BY_BOSS_AND_RAID[props.raid.slug]?.[defeatedEncounter.boss.slug];

    const label =
      cp && props.placementByBoss && props.placementByBoss[defeatedEncounter.boss.slug] ? (
        <div className="versus-timeline-widget--node-label">
          <CheckpointDetails
            checkpoint={cp}
            place={props.placementByBoss[defeatedEncounter.boss.slug]}
            hideEarnings={props.hideEarnings}
          />
        </div>
      ) : null;

    const bossNode = (
      <div
        key={defeatedEncounter.boss.encounterId}
        className="versus-timeline-widget--node-boss widget-anim--fade-in-fast"
        style={{
          left: (defeatedEncounter.timelineFraction * 100).toFixed(3) + '%'
        }}
      >
        <div className="versus-timeline-widget--node-spike slds-text-align--center">
          <span style={{ color: props.guild.color || '#fff', fontSize: 8, lineHeight: 0 }}>
            <i className="fa fa-circle" />
          </span>
          <div
            className="versus-timeline-widget--node-spike-bar"
            style={{
              borderImage: `linear-gradient(to ${props.side}, ${props.guild.color || '#fff'}, rgba(0, 0, 0, 0)) 1 100%`
            }}
          >
            {props.lifelines && props.lifelines.includes(defeatedEncounter.boss.slug) && (
              <i
                className="versus-timeline-widget--node-lifeline fa fa-solid fa-heart-pulse"
                style={{
                  color: props.guild.color
                }}
              ></i>
            )}
          </div>
        </div>

        <ComplexTooltip
          placement="bottom"
          tooltip={
            <>
              <div className="slds-text-heading--small slds-m-bottom--x-small">
                <div className="text-bold">{defeatedEncounter.boss.name}</div>
                <div>
                  {defeatedEncounter.pullCount} Pull{defeatedEncounter.pullCount !== 1 ? 's' : ''}
                </div>
              </div>
              <span className="text-raiderio">Click for Group Comp</span>
            </>
          }
          style={{ cursor: 'pointer' }}
          onClick={(domEvent: any) =>
            navigateTo(
              `/raid-logged-encounters/${props.raid.slug}/${defeatedEncounter.loggedEncounterId}`,
              // `${props.guild.path}/raid-encounters/mythic/${props.raid.slug}/${defeatedEncounter.boss.slug}`,
              domEvent
            )
          }
        >
          <img src={asset(`/images/${props.raid.slug}/portraits/${defeatedEncounter.boss.slug}.png`)} width={80} />
        </ComplexTooltip>

        {label}
      </div>
    );

    columns.push(bossNode);
  }

  return (
    <div
      style={{
        height: '10rem',
        ...props.style
        /*, top: props.side === 'top' ? '-1.5rem' : '1.5rem'*/
      }}
      className={cx('slds-is-relative', props.className)}
    >
      {columns}
    </div>
  );
}

// let refreshCount = 0;

/**
 * StreamWidgetVersusTimelinePage
 */
function StreamWidgetVersusTimelinePage(props: TStreamWidgetVersusProps) {
  const location = useLocation();

  const [data, setData] = useState(null as any);
  useWidgetVersion(data?.widgetVersion);
  const qParams = qs.parse(props.queryOverride || location.search, { ignoreQueryPrefix: true });

  const isTestMode = safeParseInt((qParams as any).test, 0);
  if (isTestMode) {
    Object.assign(qParams as any, {
      raid: TEST_PAYLOAD.guilds[0].raid.slug
    });
  }

  let simulateTime: null | moment.Moment = null;

  useEffect(() => {
    const baseRefreshMs = 1000 * _.clamp(qParams.refresh ? safeParseInt(qParams.refresh as string, 5) : 5, 0, 300);
    let timeoutHandle: any = null;

    const raid = RAID_METADATA_BY_SLUG[qParams.raid as string];

    const isSimulating = safeParseInt(qParams.simulate as string, 0) !== 0;

    // how much we adjust hours by when we are normalizing
    const normalizeHoursByRegion: { [key: string]: number } = {
      us: 0,
      eu: raid.expansion >= Expansions.Dragonflight ? -16 : -13,
      tw: -32,
      kr: -32,
      cn: -32
    };

    const isNormalizingTime = safeParseInt(String(qParams.normalize_times)) !== 0;
    const isNormalizingByPullStart = safeParseInt(String(qParams.normalize_by_pull_start)) !== 0;

    function processGuildResponse(allGuildsEarliestPullAt: moment.Moment | null, response: any) {
      let earliestTime: any = null;
      let latestTime: any = null;

      if (isNormalizingTime && !isNormalizingByPullStart) {
        for (const boss of response.bosses) {
          if (boss.pullStartedAt) {
            boss.pullStartedAt = moment
              .utc(boss.pullStartedAt)
              .add(normalizeHoursByRegion[response.guild.region.slug], 'hours')
              .format();
          }

          if (boss.pullEndedAt) {
            boss.pullEndedAt = moment
              .utc(boss.pullEndedAt)
              .add(normalizeHoursByRegion[response.guild.region.slug], 'hours')
              .format();
          }
        }
      }

      const allBosses = response.bosses.filter((boss: any) => {
        if (qParams.theme === 'xcc' && !XCC_ALL_BOSSES[boss.boss.slug]) {
          return false;
        }

        if (!boss.pullStartedAt || !boss.isDefeated) {
          return false;
        }

        const pullStartTime = moment.utc(boss.pullStartedAt);
        const pullEndTime = boss.pullEndedAt ? moment.utc(boss.pullEndedAt) : null;

        const latestPullTime = pullEndTime || pullStartTime;

        boss.pullIndicatorAt = pullEndTime || pullStartTime; // this is where we will show the boss on timeline

        if (!earliestTime || pullStartTime < earliestTime) {
          earliestTime = pullStartTime;
        }

        if (!latestTime || latestPullTime > latestTime) {
          latestTime = latestPullTime;
        }

        if (isSimulating && simulateTime && boss.pullIndicatorAt > simulateTime) {
          // not time to show this boss kill yet
          return false;
        }

        return true;
      });

      const encountersDefeated = allBosses.filter((p: any) => p.isDefeated);
      const inProgressBosses = allBosses.filter((p: any) => !p.isDefeated && p.pullCount > 0);

      let latestEncounter = qParams.boss ? allBosses.find((b: any) => b.boss.slug === qParams.boss) : null;
      if (!latestEncounter) {
        latestEncounter = inProgressBosses.length > 0 ? inProgressBosses[0] : allBosses[allBosses.length - 1];
      }

      const isKilled = latestEncounter?.isDefeated || false;
      const pullCount = latestEncounter?.pullCount || 0;
      const bestPercent = latestEncounter?.bestPercent || 100;

      if (allGuildsEarliestPullAt) {
        const deltaSeconds = moment.duration(allGuildsEarliestPullAt.diff(earliestTime)).asSeconds();

        if (isNormalizingTime && isNormalizingByPullStart && deltaSeconds !== 0) {
          latestTime = latestTime.add(deltaSeconds, 'seconds');

          for (const boss of response.bosses) {
            if (boss.pullStartedAt) {
              boss.pullStartedAt = moment.utc(boss.pullStartedAt).add(deltaSeconds, 'seconds').format();
            }

            if (boss.pullEndedAt) {
              boss.pullEndedAt = moment.utc(boss.pullEndedAt).add(deltaSeconds, 'seconds').format();
            }

            if (boss.pullIndicatorAt) {
              boss.pullIndicatorAt = moment.utc(boss.pullIndicatorAt).add(deltaSeconds, 'seconds');
            }
          }
        }
      }

      return {
        raid,
        guild: response.guild,
        allBosses,
        encountersDefeated,
        inProgressBosses,
        latestEncounter,
        pullCount,
        isKilled,
        bestPercent,
        earliestTime,
        latestTime
      };
    }

    async function refreshData() {
      // ++refreshCount;

      if (isTestMode) {
        // we can use stub data here for just seeing how the widget looks
        setData({
          ...TEST_PAYLOAD,
          guilds: TEST_PAYLOAD.guilds.map(processGuildResponse.bind(null, null))
        });
        return;
      }

      const guildIds = ((qParams.guild_ids as string) || '').split(',');

      const guildResponses: any[] = [];
      let error = false;

      for (const guildId of guildIds) {
        try {
          const response = await apiServiceAsync.viewLiveTrackingRaidProgress({
            guild_id: guildId,
            ..._.pick(qParams, 'raid', 'difficulty', 'access_key', 'period')
          });

          guildResponses.push(response);
        } catch (err) {
          console.error({ err }, 'Error caught retrieving boss progress');
          error = true;
        }
      }

      const refreshTimeMs = isSimulating ? 100 : baseRefreshMs;

      if (error && guildResponses.length < 2) {
        timeoutHandle = setTimeout(refreshData, refreshTimeMs);
        return;
      }

      if (isSimulating) {
        if (!simulateTime) {
          const lowestTimeUnix = _.min<number>(
            _.compact(
              guildResponses.map((gr) => {
                if (!gr.bosses) {
                  return null;
                }
                // pull indicator will not be set yet
                return moment.utc(gr.bosses[0].pullEndedAt || gr.bosses[0].pullStartedAt).unix();
              })
            )
          );
          if (lowestTimeUnix) {
            simulateTime = moment.unix(lowestTimeUnix).subtract(48, 'hours');
          }
        } else {
          // advance the simulation time
          simulateTime = simulateTime.add(6, 'hours');
        }
      }

      const widgetVersion = _.min(guildResponses.map((gr) => gr.widgetVersion || -1));
      const allGuildsEarliestPullAt = _.flatten(
        guildResponses.map((gr) =>
          gr.bosses.filter((b: any) => b.pullStartedAt && b.isDefeated).map((b: any) => moment.utc(b.pullStartedAt))
        )
      ).sort((a: any, b: any) => a.diff(b))[0];
      const guilds: any[] = guildResponses.map(processGuildResponse.bind(null, allGuildsEarliestPullAt));

      let earliestTime: any = null;
      let latestTime: any = null;
      for (const guild of guilds) {
        if (guild.earliestTime && (!earliestTime || guild.earliestTime < earliestTime)) {
          earliestTime = guild.earliestTime;
        }
        if (guild.latestTime && (!latestTime || guild.latestTime > latestTime)) {
          latestTime = guild.latestTime;
        }
      }

      const timelineMinHours = safeParseFloat((qParams as any).timeline_min_hours, 0);
      const totalTimeSec = Math.max(
        Math.round(timelineMinHours * 3600),
        moment.duration(latestTime - earliestTime).asSeconds()
      );

      for (const guild of guilds) {
        for (const boss of guild.allBosses) {
          if (boss.pullIndicatorAt) {
            const bossKillTimeSec = moment.duration(boss.pullIndicatorAt - earliestTime).asSeconds();
            boss.timelineFraction = bossKillTimeSec / totalTimeSec;
          }
        }
      }

      setData({
        guilds,
        numEncounters: raid.numEncounters,
        widgetVersion
      });

      if (refreshTimeMs > 0) {
        timeoutHandle = setTimeout(refreshData, refreshTimeMs);
      }
    }

    refreshData();

    return () => {
      clearTimeout(timeoutHandle);
    };
  }, []);

  if (!data) {
    return null;
  }

  // if (data.guilds.length !== 2) {
  //   // wrong number of guilds supplied
  //   return null;
  // }

  const parts: any[] = [];

  const xccEarnings = qParams.theme === 'xcc' ? processXCCEarnings(data.guilds) : null;

  const hideEarnings = safeParseInt(qParams.hide_earnings as string, 0) !== 0;

  if (data.guilds.length === 2) {
    parts.push(
      <TimelineWidgetRow
        key="top"
        side="top"
        guildLogo={qParams.guild0_logo_url || data.guilds[0].guild.logo}
        guildName={data.guilds[0].guild.displayName}
        guild={data.guilds[0]}
        lifelines={qParams.guild0_lifelines ? (qParams.guild0_lifelines as string).split(',') : []}
        placementByBoss={xccEarnings?.placementByGuildAndBoss[0]}
        hideEarnings={hideEarnings}
      />
    );

    parts.push(
      <div
        className="versus-timeline-widget--bar"
        // style={{
        //   marginLeft: 90,
        //   marginRight: 4,
        //   backgroundColor: '#52ddf4',
        //   borderRadius: 5,
        //   height: 2,
        //   boxShadow: 'rgb(82 221 244) 0px 0px 15px 3px'
        // }}
      />
    );

    parts.push(
      <TimelineWidgetRow
        key="bottom"
        side="bottom"
        guildLogo={qParams.guild1_logo_url || data.guilds[1].guild.logo}
        guildName={data.guilds[1].guild.name}
        guild={data.guilds[1]}
        lifelines={qParams.guild1_lifelines ? (qParams.guild1_lifelines as string).split(',') : []}
        placementByBoss={xccEarnings?.placementByGuildAndBoss[1]}
        hideEarnings={hideEarnings}
      />
    );
  }

  return (
    <div
      className={cx(
        'widget-container widget-anim--fade-in slds-p-around--small slds-p-left--large slds-p-right--xx-large rio-no-select',
        {
          [`widget-theme-${qParams.theme}`]: qParams.theme,
          ['widget-theme-default']: !qParams.theme
        }
      )}
      style={
        qParams.chromargb
          ? {
              backgroundImage: 'none',
              backgroundColor: qParams.chromargb === 'transparent' ? 'transparent' : qParams.chromargb.toString()
            }
          : {}
      }
    >
      <div>{parts}</div>
    </div>
  );
}

export default StreamWidgetVersusTimelinePage;
